import { useEffect } from 'react';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_SQUARE_IMAGE,
  DEFAULT_OG_SQUARE_IMAGE_SIZE,
  SITE_NAME,
} from '../seo';

const MANAGED_SELECTOR = 'data-shopsmt-seo';

function upsertMeta(attr, key, content, index = 0) {
  if (content == null || content === '') {
    return;
  }
  const matching = document.head.querySelectorAll(`meta[${attr}="${key}"]`);
  let el = matching[index];
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    if (index > 0) {
      el.setAttribute('data-shopsmt-seo-index', String(index));
    }
    document.head.appendChild(el);
  }
  el.setAttribute(MANAGED_SELECTOR, '1');
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) {
    return;
  }
  let el = document.head.querySelector(`link[rel="${rel}"][${MANAGED_SELECTOR}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED_SELECTOR, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  const scriptId = `shopsmt-jsonld-${id}`;
  let el = document.getElementById(scriptId);
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = scriptId;
    el.setAttribute(MANAGED_SELECTOR, '1');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeManagedNodes() {
  document.head.querySelectorAll(`[${MANAGED_SELECTOR}]`).forEach((node) => {
    node.remove();
  });
}

/**
 * @param {{
 *   title?: string,
 *   description?: string,
 *   canonicalPath?: string,
 *   image?: string,
 *   robots?: string,
 *   jsonLd?: object|object[],
 * }} props
 */
export function PageHead({
  title = SITE_NAME,
  description = DEFAULT_DESCRIPTION,
  canonicalPath,
  image = DEFAULT_OG_IMAGE,
  robots,
  jsonLd,
}) {
  const jsonLdKey = jsonLd == null ? '' : JSON.stringify(jsonLd);

  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:width', String(DEFAULT_OG_IMAGE_WIDTH));
    upsertMeta('property', 'og:image:height', String(DEFAULT_OG_IMAGE_HEIGHT));
    upsertMeta('property', 'og:image:type', 'image/png');
    upsertMeta('property', 'og:image', DEFAULT_OG_SQUARE_IMAGE, 1);
    upsertMeta('property', 'og:image:width', String(DEFAULT_OG_SQUARE_IMAGE_SIZE), 1);
    upsertMeta('property', 'og:image:height', String(DEFAULT_OG_SQUARE_IMAGE_SIZE), 1);
    upsertMeta('property', 'og:image:type', 'image/png', 1);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    if (canonicalPath) {
      const href = canonicalPath.startsWith('http')
        ? canonicalPath
        : `https://shops.mt${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
      upsertLink('canonical', href);
      upsertMeta('property', 'og:url', href);
    }

    if (robots) {
      upsertMeta('name', 'robots', robots);
    }

    const blocks = jsonLd == null ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    blocks.forEach((block, index) => {
      upsertJsonLd(String(index), block);
    });

    return () => {
      removeManagedNodes();
    };
  }, [title, description, canonicalPath, image, robots, jsonLdKey, jsonLd]);

  return null;
}
